import { ActionIcon, Alert, Anchor, Button, Group, Menu, Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { AdPlatform } from '@marketing-tool/shared';
import {
    IconChevronRight,
    IconCopy,
    IconDotsVertical,
    IconExternalLink,
    IconPencil,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { StatusBadge } from '../../components/StatusBadge';
import { Table } from '../../components/Table';
import { useCampaigns } from '../../data/game-report';
import type { Campaign } from '../../data/game-report/types';
import * as remote from '../../data/remote-data';
import { trpc } from '../../lib/trpc';
import { BulkEditModal } from '../../platforms/line/components/BulkEditModal';
import type { BulkEditFormData } from '../../platforms/line/components/BulkEditModal';
import { CreateCampaignForm } from '../../platforms/line/components/CreateCampaignForm';
import type { CreateCampaignFormData } from '../../platforms/line/components/CreateCampaignForm';
import { EditCampaignForm } from '../../platforms/line/components/EditCampaignForm';
import type { EditCampaignFormData } from '../../platforms/line/components/EditCampaignForm';

import { confirmAndDeleteTrpc, OperationErrorAlert } from './mutations';
import type { OperationError } from './mutations';

type CampaignsTabContentProps = {
    gameId: string;
    platform: AdPlatform;
    onDataLoaded?: (campaigns: Campaign[]) => void;
    getCampaignUrl: (campaignId: string | number) => string;
    onCampaignClick: (campaign: Campaign) => void;
};

export const CampaignsTabContent = ({
    gameId,
    platform,
    onDataLoaded,
    getCampaignUrl,
    onCampaignClick,
}: CampaignsTabContentProps) => {
    const campaigns = useCampaigns(gameId, platform);

    useEffect(() => {
        if (campaigns.data.tag === 'Success' && onDataLoaded) {
            onDataLoaded(campaigns.data.value);
        }
    }, [campaigns.data, onDataLoaded]);

    // --- Modal state ---
    const [createModal, setCreateModal] = useState<{ source?: Campaign } | null>(null);
    const [editing, setEditing] = useState<Campaign | null>(null);
    const [bulkEditing, setBulkEditing] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

    // --- Per-operation error/loading state ---
    const [createError, setCreateError] = useState<OperationError>(null);
    const [editError, setEditError] = useState<OperationError>(null);
    const [bulkEditError, setBulkEditError] = useState<OperationError>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Handlers ---

    const closeCreate = () => {
        setCreateModal(null);
        setCreateError(null);
    };

    const handleCreate = async (data: CreateCampaignFormData) => {
        setIsSubmitting(true);
        setCreateError(null);
        try {
            await trpc.line.campaigns.create.mutate({
                gameId,
                name: data.name,
                startDate: data.startDate,
                endDate: data.endDate,
                optimizationType: data.optimizationType,
                spendingLimitType: data.spendingLimitType,
                spendingLimitMicro: data.spendingLimitMicro,
            });
            notifications.show({ title: 'Success', message: 'Campaign created', color: 'green' });
            closeCreate();
            void campaigns.load();
        } catch (error) {
            setCreateError({
                message: error instanceof Error ? error.message : 'Failed to create campaign. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeEdit = () => {
        setEditing(null);
        setEditError(null);
    };

    const handleUpdate = async (data: EditCampaignFormData) => {
        setIsSubmitting(true);
        setEditError(null);
        try {
            const { id, ...updateData } = data;
            await trpc.line.campaigns.update.mutate({
                gameId,
                updates: { [id]: updateData },
            });
            notifications.show({ title: 'Success', message: 'Campaign updated', color: 'green' });
            closeEdit();
            void campaigns.load();
        } catch (error) {
            setEditError({
                message: error instanceof Error ? error.message : 'Failed to update campaign. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (campaignId: string | number) =>
        confirmAndDeleteTrpc({
            title: 'Delete campaign',
            message: 'Are you sure you want to delete this campaign? This action cannot be undone.',
            deleteFn: () => trpc.line.campaigns.delete.mutate({ gameId, campaignIds: [String(campaignId)] }),
            successMessage: 'Campaign deleted',
            onSuccess: () => void campaigns.load(),
        });

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        confirmAndDeleteTrpc({
            title: 'Delete campaigns',
            message: `Are you sure you want to delete ${selectedIds.size} campaign(s)? This action cannot be undone.`,
            deleteFn: () =>
                trpc.line.campaigns.delete.mutate({ gameId, campaignIds: Array.from(selectedIds).map(String) }),
            successMessage: `${selectedIds.size} campaign(s) deleted`,
            onSuccess: () => {
                setSelectedIds(new Set());
                void campaigns.load();
            },
        });
    };

    const closeBulkEdit = () => {
        setBulkEditing(false);
        setBulkEditError(null);
    };

    const handleBulkUpdate = async (data: BulkEditFormData) => {
        setIsSubmitting(true);
        setBulkEditError(null);
        try {
            const updates = Object.fromEntries(Array.from(selectedIds).map((id) => [id, data]));
            await trpc.line.campaigns.update.mutate({ gameId, updates });
            notifications.show({ title: 'Success', message: 'Campaigns updated', color: 'green' });
            closeBulkEdit();
            setSelectedIds(new Set());
            void campaigns.load();
        } catch (error) {
            setBulkEditError({
                message: error instanceof Error ? error.message : 'Failed to update campaigns. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render ---

    if (remote.isNotAsked(campaigns.data)) return null;
    if (remote.isFailure(campaigns.data)) {
        return (
            <Alert color="red" title="Error loading campaigns">
                {campaigns.data.value}
            </Alert>
        );
    }

    return (
        <>
            <Group justify="space-between" mb="md">
                <Text size="sm" fw={600}>
                    Campaigns
                </Text>
                <Button size="sm" onClick={() => setCreateModal({})} leftSection={<IconPlus size={16} />}>
                    Create Campaign
                </Button>
            </Group>

            <Table
                storageKey="campaigns"
                loading={remote.isLoading(campaigns.data)}
                id={(campaign) => campaign.id}
                density="xs"
                columns={[
                    {
                        name: 'Campaign',
                        render: (campaign) => (
                            <Anchor
                                href={getCampaignUrl(campaign.id)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onCampaignClick(campaign);
                                }}
                                underline="hover"
                                fw={600}
                                inherit
                                style={{ minWidth: 0 }}
                            >
                                <Group gap={4} wrap="nowrap">
                                    <Text truncate inherit>
                                        {campaign.name}
                                    </Text>
                                    <IconChevronRight size={14} style={{ flexShrink: 0 }} />
                                </Group>
                            </Anchor>
                        ),
                        sort: (a, b) => a.name.localeCompare(b.name),
                    },
                    {
                        name: 'Status',
                        type: 'status',
                        width: 120,
                        render: (campaign) => {
                            const status =
                                'deliveryStatus' in campaign ? campaign.deliveryStatus : campaign.effectiveStatus;
                            return <StatusBadge status={status} />;
                        },
                        sort: (a, b) => {
                            const statusA = 'deliveryStatus' in a ? a.deliveryStatus : a.effectiveStatus;
                            const statusB = 'deliveryStatus' in b ? b.deliveryStatus : b.effectiveStatus;
                            return statusA.localeCompare(statusB);
                        },
                    },
                    {
                        name: 'Start Date',
                        type: 'date',
                        width: 120,
                        render: (campaign) => new Date(campaign.startDate).toLocaleDateString(),
                        sort: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
                    },
                    {
                        name: 'End Date',
                        type: 'date',
                        width: 120,
                        render: (campaign) => new Date(campaign.endDate).toLocaleDateString(),
                        sort: (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
                    },
                    {
                        name: 'Creation Date',
                        type: 'date',
                        width: 120,
                        render: (campaign) => new Date(campaign.createdDate).toLocaleDateString(),
                        sort: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
                    },
                    {
                        name: 'Actions',
                        type: 'actions',
                        width: 50,
                        render: (campaign) => (
                            <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                    <ActionIcon
                                        variant="subtle"
                                        aria-label="Row actions"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconDotsVertical size={16} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {campaign.externalAdsManagerUrl && (
                                        <Menu.Item
                                            component="a"
                                            href={campaign.externalAdsManagerUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            leftSection={<IconExternalLink size={14} />}
                                        >
                                            Open in Ads Manager
                                        </Menu.Item>
                                    )}
                                    <Menu.Item
                                        leftSection={<IconCopy size={14} />}
                                        onClick={() => setCreateModal({ source: campaign })}
                                    >
                                        Duplicate
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconPencil size={14} />}
                                        onClick={() => setEditing(campaign)}
                                    >
                                        Edit
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item
                                        leftSection={<IconTrash size={14} />}
                                        color="red"
                                        onClick={() => void handleDelete(campaign.id)}
                                    >
                                        Delete
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        ),
                    },
                ]}
                data={remote.withDefault(campaigns.data, [])}
                defaultSortColumn="Campaign"
                pagination={{ pageSize: 20, showPagination: true }}
                selection={{
                    selectedIds,
                    onSelectionChange: setSelectedIds,
                    actions: [
                        {
                            label: 'Edit Selected',
                            onClick: () => setBulkEditing(true),
                            variant: 'outline',
                            startIcon: <IconPencil size={16} />,
                        },
                        {
                            label: 'Delete Selected',
                            onClick: handleBulkDelete,
                            variant: 'filled',
                            color: 'red',
                            startIcon: <IconTrash size={16} />,
                        },
                    ],
                }}
            />

            {/* Create / Duplicate Campaign */}
            <Modal
                opened={createModal !== null}
                onClose={closeCreate}
                size="lg"
                title={createModal?.source ? 'Duplicate Campaign' : 'Create Campaign'}
            >
                <OperationErrorAlert error={createError} title="Failed to create campaign" />
                {platform && (
                    <CreateCampaignForm
                        platform={platform}
                        onSubmit={handleCreate}
                        onCancel={closeCreate}
                        isSubmitting={isSubmitting}
                        initialData={
                            createModal?.source
                                ? {
                                      name: `${createModal.source.name} (Copy)`,
                                      startDate: createModal.source.startDate,
                                      endDate: createModal.source.endDate,
                                  }
                                : undefined
                        }
                    />
                )}
            </Modal>

            {/* Edit Campaign */}
            <Modal opened={editing !== null} onClose={closeEdit} size="lg" title="Edit Campaign">
                <OperationErrorAlert error={editError} title="Failed to update campaign" />
                {platform && editing && (
                    <EditCampaignForm
                        platform={platform}
                        campaign={editing}
                        onSubmit={handleUpdate}
                        onCancel={closeEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>

            {/* Bulk Edit Campaigns */}
            <Modal opened={bulkEditing} onClose={closeBulkEdit} size="lg" title="Bulk Edit Campaigns">
                <OperationErrorAlert error={bulkEditError} title="Failed to update campaigns" />
                {platform && (
                    <BulkEditModal
                        platform={platform}
                        entityType="campaign"
                        selectedCount={selectedIds.size}
                        onSubmit={handleBulkUpdate}
                        onCancel={closeBulkEdit}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Modal>
        </>
    );
};
